import { usePermissions, type Permission } from '@nucleus-commerce/admin-kit/permissions';

import { useStoreOrNull, type Store } from './store-context';

export type ReadOnlyReason = 'closed' | 'permission';

export interface StoreEditable {
  editable: boolean;
  /** Why the screen is read-only; null when it is editable. A closed store wins over a missing permission. */
  reason: ReadOnlyReason | null;
}

export interface StoreEditableOptions {
  /** The store whose status decides; defaults to the one from the `/s/$storeId` route. */
  store?: Pick<Store, 'status'> | null | undefined;
  /** Permission(s) a change needs; the backend refuses writes without them. */
  permission: Permission | Permission[];
  mode?: 'all' | 'any';
}

/**
 * The backend refuses every change to a closed store (409) and every write without the right
 * permission (403). Settings screens ask this once and disable their forms instead of letting
 * the person fill everything in and fail on Save.
 */
export function useStoreEditable({
  store,
  permission,
  mode = 'all',
}: StoreEditableOptions): StoreEditable {
  const { can } = usePermissions();
  const contextStore = useStoreOrNull();
  const status = (store ?? contextStore)?.status;
  if (status === 'closed') return { editable: false, reason: 'closed' };
  if (!can(permission, mode)) return { editable: false, reason: 'permission' };
  return { editable: true, reason: null };
}
